import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';

// Ícones do Material UI (você pode usar outros à vontade)
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import ChatIcon from '@mui/icons-material/Chat';
import AttachFileIcon from '@mui/icons-material/AttachFile';

function EditActivityModal({ open, onClose, card, onSave }) {
  // Estados para os campos do card
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [font, setFont] = useState('Arial');

  // Estados para comentários
  const [comentarios, setComentarios] = useState('');
  const [allComments, setAllComments] = useState([]);

  // Estados para anexos
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadedVideos, setUploadedVideos] = useState([]);

  // Ao abrir o modal ou mudar o card, popula os campos
  useEffect(() => {
    if (card) {
      setNome(card.name || '');
      setDescricao(card.description || '');
      setFont(card.font || 'Arial');
      // Se já houver lista de comentários ou anexos, carrega; senão, array vazio
      setAllComments(card.allComments || []); 
      setUploadedImages(card.images || []);
      setUploadedVideos(card.videos || []);
    }
  }, [card]);

  // Salvar alterações (chamado ao clicar no botão "Salvar")
  const handleSave = () => {
    const updatedCard = {
      ...card,
      name: nome,
      description: descricao,
      font,
      allComments,
      images: uploadedImages,
      videos: uploadedVideos
    };
    onSave(updatedCard);
  };

  // Lógica para adicionar um novo comentário na lista
  const handleAddComment = () => {
    if (!comentarios.trim()) return;
    const newComment = {
      id: Date.now(),      // id simples, ou use outro gerador
      text: comentarios,
      date: new Date()     // data/hora atual
    };
    setAllComments(prev => [newComment, ...prev]);
    setComentarios(''); // limpa o campo
  };

  // Upload de imagens
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadedImages(files);
  };

  // Upload de vídeos
  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadedVideos(files);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      {/* Título do modal (padrão do MUI) */}
      <DialogTitle sx={{ fontSize: 18, fontWeight: 'bold' }}>
        {nome || 'Editar Card'}
      </DialogTitle>

      <DialogContent dividers>
        {/* Seção de Título do Card */}
        <Box display="flex" flexDirection="row" alignItems="center" mb={2}>
          <DescriptionIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flex: 1 }}>
            Título do Card
          </Typography>
        </Box>
        <TextField
          fullWidth
          placeholder="Nome/Título da atividade"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Seção de Descrição */}
        <Divider sx={{ my: 2 }} />
        <Box display="flex" flexDirection="row" alignItems="center" mb={1}>
          <DescriptionIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Descrição</Typography>
        </Box>
        <TextField
          fullWidth
          multiline
          minRows={4}
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder="Detalhes sobre esta atividade..."
          inputProps={{
            style: { fontFamily: font }
          }}
        />

        {/* Seletor de fonte (opcional) */}
        <FormControl fullWidth sx={{ my: 2 }}>
          <InputLabel id="font-select-label">Fonte da Descrição</InputLabel>
          <Select
            labelId="font-select-label"
            value={font}
            label="Fonte da Descrição"
            onChange={(e) => setFont(e.target.value)}
          >
            <MenuItem value="Arial">Arial</MenuItem>
            <MenuItem value="Times New Roman">Times New Roman</MenuItem>
            <MenuItem value="Courier New">Courier New</MenuItem>
            <MenuItem value="Verdana">Verdana</MenuItem>
            <MenuItem value="Tahoma">Tahoma</MenuItem>
          </Select>
        </FormControl>

        {/* Seção de Anexos */}
        <Divider sx={{ my: 2 }} />
        <Box display="flex" flexDirection="row" alignItems="center" mb={1}>
          <AttachFileIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Anexos</Typography>
        </Box>

        {/* Upload de imagens */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <ImageIcon />
          <Button variant="outlined" component="label">
            Selecionar Fotos
            <input
              type="file"
              accept="image/*"
              hidden
              multiple
              onChange={handleImageUpload}
            />
          </Button>
          {uploadedImages?.length > 0 && (
            <Typography variant="body2">
              {uploadedImages.length} foto(s) selecionada(s)
            </Typography>
          )}
        </Box>

        {/* Upload de vídeos */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <VideoCameraBackIcon />
          <Button variant="outlined" component="label">
            Selecionar Vídeos
            <input
              type="file"
              accept="video/*"
              hidden
              multiple
              onChange={handleVideoUpload}
            />
          </Button>
          {uploadedVideos?.length > 0 && (
            <Typography variant="body2">
              {uploadedVideos.length} vídeo(s) selecionado(s)
            </Typography>
          )}
        </Box>

        {/* Seção de Comentários (atividade) */}
        <Divider sx={{ my: 2 }} />
        <Box display="flex" flexDirection="row" alignItems="center" mb={1}>
          <ChatIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Atividade</Typography>
        </Box>

        {/* Campo para digitar um novo comentário */}
        <Box display="flex" flexDirection="row" gap={1} mb={2}>
          <TextField
            fullWidth
            placeholder="Escreva um comentário..."
            value={comentarios}
            onChange={(e) => setComentarios(e.target.value)}
          />
          <Button variant="contained" onClick={handleAddComment}>
            Comentar
          </Button>
        </Box>

        {/* Lista de comentários (feed de atividade) */}
        {allComments?.length > 0 && (
          <Box
            sx={{
              maxHeight: 200,
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 1,
              p: 1
            }}
          >
            {allComments.map((comm) => (
              <Box
                key={comm.id}
                mb={1}
                sx={{ backgroundColor: '#f9f9f9', p: 1, borderRadius: 1 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Usuário (ID: {comm.id})
                </Typography>
                <Typography variant="body2">{comm.text}</Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  {new Date(comm.date).toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" onClick={handleSave}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditActivityModal;
